// Monthly CapEx/OpEx rollup for a board's sprint history.
//
// Capitalization is computed and persisted PER SPRINT (a sprint-level CapEx/OpEx
// split, never date-stamped per ticket). Finance, however, reports by calendar
// month. We bridge the two with the cheapest faithful approximation the chosen
// design allows: bucket each sprint into the month it *ends* in and sum the raw
// CapEx/OpEx counts already in the row.
//
// This is "CapEx of sprints ending in month X", NOT strict per-ticket calendar
// dating — a sprint straddling a month boundary lands entirely in its end
// month. The SPA labels it as such so no one mistakes it for ticket-level
// accrual. Percentages are recomputed from summed numerators/denominators
// (never averaged across sprints — averaging percentages is wrong when sprints
// differ in size).

import type { SprintListItem } from "@/api/types";

export interface CapexMonthBucket {
  month: string; // "2026-06" — sortable key
  label: string; // "Jun 2026" — display
  capexCount: number;
  opexCount: number;
  totalCount: number;
  capexSp: number;
  opexSp: number;
  totalSp: number;
  // null when the denominator is 0 — render as "—", never "0%".
  countPct: number | null;
  spPct: number | null;
  sprintCount: number;
  // Sprints that contributed despite the tenant lacking a Capatizable field
  // (capitalization.applicable === false) are excluded entirely, so every
  // bucket here is backed by genuinely classified work.
}

function monthKey(iso: string): string | null {
  // sprint_end is an ISO date/datetime string; take the YYYY-MM prefix without
  // constructing a Date (avoids TZ drift on date-only strings).
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  return m ? `${m[1]}-${m[2]}` : null;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function monthLabel(key: string): string {
  const [y, m] = key.split("-");
  const idx = Number(m) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS[idx]} ${y}` : key;
}

function pct(part: number, total: number): number | null {
  return total === 0 ? null : part / total;
}

/**
 * Bucket sprints into calendar months by their end date and sum the persisted
 * CapEx/OpEx counts. Returns buckets sorted oldest → newest. Sprints with no
 * parseable `sprint_end`, or whose capitalization is absent / not applicable,
 * are skipped.
 */
export function capexByMonth(sprints: SprintListItem[]): CapexMonthBucket[] {
  const acc = new Map<string, CapexMonthBucket>();

  for (const s of sprints) {
    const cap = s.capitalization;
    if (!cap || !cap.applicable) continue;
    const key = s.sprint_end ? monthKey(s.sprint_end) : null;
    if (!key) continue;

    let b = acc.get(key);
    if (!b) {
      b = {
        month: key,
        label: monthLabel(key),
        capexCount: 0, opexCount: 0, totalCount: 0,
        capexSp: 0, opexSp: 0, totalSp: 0,
        countPct: null, spPct: null,
        sprintCount: 0,
      };
      acc.set(key, b);
    }
    b.capexCount += cap.by_count.capex;
    b.opexCount += cap.by_count.opex;
    b.totalCount += cap.by_count.total;
    b.capexSp += cap.by_sp.capex;
    b.opexSp += cap.by_sp.opex;
    b.totalSp += cap.by_sp.total;
    b.sprintCount += 1;
  }

  const out = [...acc.values()].sort((a, b) => a.month.localeCompare(b.month));
  for (const b of out) {
    b.countPct = pct(b.capexCount, b.totalCount);
    b.spPct = pct(b.capexSp, b.totalSp);
  }
  return out;
}
